import { useCallback, useEffect, useRef, useState } from 'react';
import { CLOCK_REGION, clockRegionRect } from './gameClock/clockReader';
import { STATUS_REGION, statusRegionRect } from './gameStatus/statusReader';
import { GAME_TIME_REGION, gameTimeRegionRect } from './gameTime/gameTimeReader';
import { WICKELINE_REGION, wickelineRegionRect } from './gameEvents/wickelineDetector';

export function isScreenCaptureSupported() {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
}

/**
 * 게임 창 화면 공유(getDisplayMedia)와 프레임 캡처.
 * 공유한 화면은 숨겨진 <video>(videoRef)로 받고, 캡처할 때 그 시점의 프레임을 원본 해상도 PNG로 만든다.
 * 게임 프로세스에는 접근하지 않고 브라우저가 받은 화면 영상만 쓴다.
 */
export default function useScreenCapture() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  // <video ref={attachVideo}>로 쓴다. 게임 시작/종료로 화면 구성이 바뀌어 video가 새로 만들어져도 이미 받고 있는 공유 화면을 바로 연결한다.
  const attachVideo = useCallback((element) => {
    videoRef.current = element;
    if (element && streamRef.current && element.srcObject !== streamRef.current) {
      element.srcObject = streamRef.current;
      element.play().catch(() => {});
    }
  }, []);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState(null);

  const stop = useCallback(() => {
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.onended = null;
        track.stop();
      });
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setSharing(false);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    if (!isScreenCaptureSupported()) {
      setError('이 브라우저는 화면 공유를 지원하지 않아요. PC의 크롬/엣지에서 열어 주세요.');
      return false;
    }
    try {
      // 창 단위 공유를 권장한다 (PiP 창이 캡처 화면에 섞이지 않고, 게임 창만 받는다)
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'window', frameRate: { ideal: 5, max: 10 } },
        audio: false,
        selfBrowserSurface: 'exclude',
      });
      streamRef.current = stream;
      const [track] = stream.getVideoTracks();
      // 사용자가 브라우저의 "공유 중지"를 눌렀을 때도 상태를 맞춘다
      track.onended = () => stop();
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setSharing(true);
      return true;
    } catch (e) {
      setError(e.name === 'NotAllowedError' ? '화면 공유가 취소되었어요.' : `화면 공유를 시작하지 못했어요. (${e.name})`);
      return false;
    }
  }, [stop]);

  // 지금 공유 중인 화면의 프레임을 원본 해상도 PNG로 캡처한다.
  const captureFrame = useCallback(async () => {
    const video = videoRef.current;
    if (!streamRef.current || !video || !video.videoWidth) {
      throw new Error('공유 중인 게임 화면이 없어요. 먼저 화면 공유를 시작해 주세요.');
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('캡처 이미지를 만들지 못했어요.'))), 'image/png');
    });
    return { blob, url: URL.createObjectURL(blob), width: canvas.width, height: canvas.height, capturedAt: new Date() };
  }, []);

  // 화면에서 작은 영역만 오려 기준 크기의 픽셀로 돌려준다(게임 시계, 남은 팀/인원 패널). 프레임 전체를 복사하지 않아 자주 불러도 가볍다.
  const regionCanvasesRef = useRef({});
  const captureRegionImage = useCallback((name, region, rectOf) => {
    const video = videoRef.current;
    if (!streamRef.current || !video || !video.videoWidth) {
      return null;
    }
    if (!regionCanvasesRef.current[name]) {
      const canvas = document.createElement('canvas');
      canvas.width = region.width;
      canvas.height = region.height;
      regionCanvasesRef.current[name] = canvas;
    }
    const canvas = regionCanvasesRef.current[name];
    const { sx, sy, sw, sh } = rectOf(video.videoWidth, video.videoHeight);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }, []);
  const captureClockImage = useCallback(() => captureRegionImage('clock', CLOCK_REGION, clockRegionRect), [captureRegionImage]);
  const captureStatusImage = useCallback(() => captureRegionImage('status', STATUS_REGION, statusRegionRect), [captureRegionImage]);
  const captureGameTimeImage = useCallback(() => captureRegionImage('gameTime', GAME_TIME_REGION, gameTimeRegionRect), [captureRegionImage]);
  const captureWickelineImage = useCallback(() => captureRegionImage('wickeline', WICKELINE_REGION, wickelineRegionRect), [captureRegionImage]);

  // 페이지를 떠나면 공유를 끝낸다
  useEffect(() => stop, [stop]);

  return { videoRef, attachVideo, sharing, error, start, stop, captureFrame, captureClockImage, captureStatusImage, captureGameTimeImage, captureWickelineImage };
}

// 샘플 수집용: 캡처한 프레임을 PNG 파일로 내려받는다.
export function frameFileName(prefix, capturedAt) {
  const pad = (n) => String(n).padStart(2, '0');
  const d = capturedAt;
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  return `${prefix}-${stamp}.png`;
}

export function downloadFrame(frame, prefix) {
  const link = document.createElement('a');
  link.href = frame.url;
  link.download = frameFileName(prefix, frame.capturedAt);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
